import { initializeApp } from 'firebase/app';

import {
  FcmOptions,
  getMessaging,
  getToken,
  MessagePayload,
  Messaging,
  NotificationPayload,
  onMessage,
  Unsubscribe,
} from 'firebase/messaging';

import { defaultToast, ToastID } from 'ui/base/toast';

import { FCMService } from 'services/accounts/fcm_service';

import { Optional } from './types';
import { FCMMessage, FCMMessageKind } from './ipc_types';
import { handleNotification, handleNotificationOpen } from './ipc';

const vapidKey = 'BNnrtOgIJMYSMCjJ66LyRWiZjwkWZMB9WfPA5m8SUeQ2bpBHjM-BcyLLum4jvyceRRuHkU2Z1HchgVKIHrmIvCo';

const firebaseConfig = {
  apiKey: 'AIzaSyCRJfY1fp-xY_8jEPOEGDuuco_DgYV4pII',
  authDomain: 'pangolin-firewall.firebaseapp.com',
  databaseURL: 'https://pangolin-firewall.firebaseio.com',
  projectId: 'pangolin-firewall',
  storageBucket: 'pangolin-firewall.firebasestorage.app',
  messagingSenderId: '836842086844',
  appId: '1:836842086844:web:cfce2686b2dd06e230cbb9',
};

declare global {
  interface Window {
    WebNotifications: WebNotifications | undefined;
  }
}

export function validateNotificationPayload(notification: unknown): notification is NotificationPayload {
  if (notification == null || typeof notification !== 'object') return false;
  const candidate = notification as Record<string, unknown>;
  if ('title' in candidate && candidate.title !== undefined && typeof candidate.title !== 'string') return false;
  if ('body' in candidate && candidate.body !== undefined && typeof candidate.body !== 'string') return false;
  if ('image' in candidate && candidate.image !== undefined && typeof candidate.image !== 'string') return false;
  if ('icon' in candidate && candidate.icon !== undefined && typeof candidate.icon !== 'string') return false;
  return true;
}

export const validateFcmOptions = (options: unknown): options is FcmOptions => {
  if (options == null || typeof options !== 'object') return false;
  const candidate = options as Record<string, unknown>;
  if ('link' in candidate && candidate.link !== undefined && typeof candidate.link !== 'string') return false;
  if ('analyticsLabel' in candidate && candidate.analyticsLabel !== undefined && typeof candidate.analyticsLabel !== 'string') return false;
  return true;
};

export function validateMessagePayload(message: unknown): message is MessagePayload {
  if (message == null || typeof message !== 'object') return false;
  const candidate = message as Record<string, unknown>;
  if (!('messageId' in candidate) || typeof candidate.messageId !== 'string') return false;
  if (!('collapseKey' in candidate) || typeof candidate.collapseKey !== 'string') return false;
  if (!('from' in candidate) || typeof candidate.from !== 'string') return false;
  if ('fcmOptions' in candidate && !validateFcmOptions(candidate.fcmOptions)) return false;
  if ('notification' in candidate && !validateNotificationPayload(candidate.notification)) return false;
  if ('data' in candidate && candidate.data !== undefined && typeof candidate.data == 'object') {
    if (candidate.data === null) return false;
    for (const value of Object.values(candidate.data)) {
      if (typeof value !== 'string') return false;
    }
  }
  return true;
}

export class WebNotifications {
  private readonly messaging: Messaging;

  initial: Optional<FCMMessage>;
  token: Optional<string>;
  unsubscribe: Optional<Unsubscribe>;
  registration: Optional<ServiceWorkerRegistration>;

  private onNotificationsRegisteredListener?: (token: string) => unknown;
  private onNotificationReceivedListener?: (message: FCMMessage) => unknown;
  private onNotificationOpenedListener?: (message: FCMMessage) => unknown;

  constructor() {
    this.messaging = getMessaging(initializeApp(firebaseConfig));
  }

  async initialize(): Promise<void> {
    const notification = getInitialWebNotification();

    if (notification?.messageId && notification?.data) {
      this.initial = convertToFCMMessage(notification);
      this.onNotificationOpenedHandler(notification);
    }

    try {
      if (!this.hasNotificationPermission()) {
        defaultToast(ToastID.ERROR_PERMISSION_NOTIFICATIONS_MISSING);
        return;
      }

      await this.registerRemoteNotifications();
    }
    catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        defaultToast(ToastID.ERROR_PERMISSION_NOTIFICATIONS_BLOCKED);
        return;
      }

      throw error;
    }
  }

  hasNotificationPermission(): boolean {
    if (typeof Notification === 'undefined') return false;
    return Notification.permission === 'granted';
  }

  async requestNotificationPermission(): Promise<boolean> {
    if (typeof Notification === 'undefined') return false;
    if (Notification.permission === 'granted') return true;
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  private async registerRemoteNotificationsBackground(): Promise<Optional<ServiceWorkerRegistration>> {
    if (!('navigator' in window) || !('serviceWorker' in window.navigator)) return;
    this.registration = await window.navigator.serviceWorker.register('/notifications.js');
    window.navigator.serviceWorker.addEventListener('message', (message) => {
      const payload = message.data;
      if (!validateMessagePayload(payload)) return;
      switch (message?.data?.type) {
        case 'push-received': {
          this.onNotificationReceivedBackgroundHandler(payload);
          return;
        }
        case 'push-opened': {
          this.onNotificationOpenedHandler(payload);
          return;
        }
        default: {
          return;
        }
      }
    });
    const worker = this.registration.installing ?? this.registration.waiting;
    if (worker) await new Promise<void>((resolve) => {
      worker.addEventListener('statechange', function onStateChange(event) {
        const target = event.target as ServiceWorker;
        if (target.state === 'activated') {
          target.removeEventListener('statechange', onStateChange);
          resolve();
        }
      });
    });
    return this.registration;
  }

  private async registerRemoteNotificationsForeground(registration: Optional<ServiceWorkerRegistration>): Promise<Optional<string>> {
    if (this.unsubscribe?.()) this.unsubscribe();
    this.token = await getToken(this.messaging, { vapidKey, serviceWorkerRegistration: registration });
    this.unsubscribe = onMessage(this.messaging, this.onNotificationReceivedForegroundHandler.bind(this));
    return this.token;
  }

  async registerRemoteNotifications(): Promise<string> {
    const registration = await this.registerRemoteNotificationsBackground();
    const token = await this.registerRemoteNotificationsForeground(registration);
    if (token) { this.onNotificationsRegisteredHandler(token); return token; }
    throw new Error('Failed to register remote notifications');
  }

  onNotificationsRegistered<T>(listener: (token: string) => T) {
    this.onNotificationsRegisteredListener = listener;
  }

  onNotificationReceived<T>(listener: (message: FCMMessage) => T) {
    this.onNotificationReceivedListener = listener;
  }

  onNotificationOpened<T>(listener: (message: FCMMessage) => T) {
    this.onNotificationOpenedListener = listener;
  }

  private onNotificationsRegisteredHandler(token: string) {
    if (this.onNotificationsRegisteredListener) {
      this.onNotificationsRegisteredListener(token);
    }
  }

  private onNotificationReceivedForegroundHandler(payload: MessagePayload) {
    if (this.onNotificationReceivedListener) {
      const message = convertToFCMMessage(payload);
      if (message != null) this.onNotificationReceivedListener(message);
    }
  }

  private onNotificationReceivedBackgroundHandler(payload: MessagePayload) {
    if (this.onNotificationReceivedListener) {
      const message = convertToFCMMessage(payload);
      if (message) this.onNotificationReceivedListener(message);
    }
  }

  private onNotificationOpenedHandler(payload: MessagePayload) {
    if (this.onNotificationOpenedListener) {
      const message = convertToFCMMessage(payload);
      if (message != null) this.onNotificationOpenedListener(message);
    }
  }
}

export type NotificationData = {
  [key: string]: string;
} | undefined;

function getInitialWebNotification(): Optional<MessagePayload> {
  const url = new URL(window.location.href);

  const query = url.searchParams;
  const encoded = query.get('initialNotification');

  if (encoded == null || encoded === '') return;

  query.delete('initialNotification');
  window.history.replaceState({}, document.title, url.toString());

  let parsed: unknown;
  try { parsed = JSON.parse(atob(encoded)); } catch (_e) { return; }

  if (!validateMessagePayload(parsed)) return;
  return parsed as MessagePayload;
}

export function validateFCMMessage(message: unknown): message is FCMMessage {
  if (message == null || typeof message !== 'object') return false;
  const candidate = message as Record<string, unknown>;
  switch (candidate.kind) {
    case FCMMessageKind.HOST_CONNECTED:
      return typeof candidate.kind === 'number'
        && typeof candidate.pangolinID === 'string'
        && typeof candidate.hostID === 'string'
        && (candidate.messageID == null || typeof candidate.messageID === 'string');
    case FCMMessageKind.NEW_INCIDENT:
      return typeof candidate.kind === 'number'
        && typeof candidate.pangolinID === 'string'
        && typeof candidate.issueID === 'string'
        && typeof candidate.incidentID === 'string'
        && (candidate.messageID == null || typeof candidate.messageID === 'string');
    case FCMMessageKind.ONLINE_STATUS_UPDATE:
      return typeof candidate.kind === 'number'
        && typeof candidate.pangolinID === 'string'
        && typeof candidate.onlineStatus === 'boolean'
        && (candidate.messageID == null || typeof candidate.messageID === 'string');
    case FCMMessageKind.UPGRADE_STATUS_UPDATE:
      return typeof candidate.kind === 'number'
        && typeof candidate.pangolinID === 'string'
        && typeof candidate.upgradeID === 'string'
        && typeof candidate.upgradeStatus === 'number'
        && (candidate.messageID == null || typeof candidate.messageID === 'string');
    case FCMMessageKind.RESTORE_STATUS_UPDATE:
      return typeof candidate.kind === 'number'
        && typeof candidate.pangolinID === 'string'
        && typeof candidate.restoreID === 'string'
        && typeof candidate.restoreStatus === 'number'
        && (candidate.messageID == null || typeof candidate.messageID === 'string');
    default:
      return false;
  }
}

export function convertToFCMMessage(payload: MessagePayload): Optional<FCMMessage> {
  const [id, data] = [payload.messageId, payload.data];
  if (data == null) return;
  switch (data.kind) {
    case 'HOST_CONNECTED': {
      const fcmMessage = {
        kind: FCMMessageKind.HOST_CONNECTED,
        messageID: id,
        pangolinID: data.pangolinID,
        hostID: data.hostID,
      };
      if (!validateFCMMessage(fcmMessage)) return;
      return fcmMessage as FCMMessage;
    }
    case 'NEW_INCIDENT': {
      return {
        kind: FCMMessageKind.NEW_INCIDENT,
        messageID: id,
        pangolinID: data.pangolinID,
        issueID: data.issueID,
        incidentID: data.incidentID,
      };
    }
    case 'ONLINE_STATUS_UPDATE': {
      const fcmMessage = {
        kind: FCMMessageKind.ONLINE_STATUS_UPDATE,
        messageID: id,
        pangolinID: data.pangolinID,
        onlineStatus: data.onlineStatus == 'true',
      };
      if (!validateFCMMessage(fcmMessage)) return;
      return fcmMessage as FCMMessage;
    }
    case 'UPGRADE_STATUS_UPDATE': {
      const fcmMessage = {
        kind: FCMMessageKind.UPGRADE_STATUS_UPDATE,
        messageID: id,
        pangolinID: data.pangolinID,
        upgradeID: data.upgradeID,
        upgradeStatus: Number(data.upgradeStatus),
      };
      if (!validateFCMMessage(fcmMessage)) return;
      return fcmMessage as FCMMessage;
    }
    case 'RESTORE_STATUS_UPDATE': {
      const fcmMessage = {
        kind: FCMMessageKind.RESTORE_STATUS_UPDATE,
        messageID: id,
        pangolinID: data.pangolinID,
        restoreID: data.restoreID,
        restoreStatus: Number(data.restoreStatus),
      };
      if (!validateFCMMessage(fcmMessage)) return;
      return fcmMessage as FCMMessage;
    }
    default: {
      return;
    }
  }
}

export async function setupWebNotifications(): Promise<void> {
  window.WebNotifications = new WebNotifications();
  window.WebNotifications.onNotificationsRegistered(FCMService.update);
  window.WebNotifications.onNotificationReceived(handleNotification);
  window.WebNotifications.onNotificationOpened(handleNotificationOpen);
  await window.WebNotifications.initialize();
}
